import { useState, useCallback, useRef, useEffect, useMemo } from "react";
import {
  getPnlValues,
  getOverviewData,
  getDriversData,
  getDimensionData,
  getTopElementsData,
  getInitiativesData,
} from "services/governanceService";

/**
 * Page data status constants.
 */
const STATUS = {
  IDLE: "idle",
  LOADING: "loading",
  READY: "ready",
  ERROR: "error",
};

/**
 * Build a prefetch queue starting from the current page.
 * Order: current page → forward pages → backward pages (closest first).
 */
function buildPrefetchQueue(sortedPageKeys, currentPageKey) {
  const currentIndex = sortedPageKeys.indexOf(currentPageKey);
  if (currentIndex === -1) return [...sortedPageKeys];

  const queue = [sortedPageKeys[currentIndex]];

  for (let i = currentIndex + 1; i < sortedPageKeys.length; i++) {
    queue.push(sortedPageKeys[i]);
  }

  for (let i = currentIndex - 1; i >= 0; i--) {
    queue.push(sortedPageKeys[i]);
  }

  return queue;
}

// ============================================================================
// Period helpers – shared by buildPeriodFilter & buildComparisonPeriods
// ============================================================================

/**
 * Return an array of numMonths periods counting backwards from latestYear/latestMonth,
 * optionally offset by `offset` months before that.
 * Each element is [year, month].
 */
function getPeriodsRange(latestYear, latestMonth, numMonths, offset = 0) {
  const periods = [];
  let y = latestYear;
  let m = latestMonth;

  // Apply offset
  for (let i = 0; i < offset; i++) {
    m--;
    if (m === 0) {
      m = 12;
      y--;
    }
  }

  // Collect periods
  for (let i = 0; i < numMonths; i++) {
    periods.push([y, m]);
    m--;
    if (m === 0) {
      m = 12;
      y--;
    }
  }

  return periods;
}

/**
 * Return YTD periods for the given year up to endMonth. Each is [year, month].
 */
function getYTDPeriods(year, endMonth) {
  const periods = [];
  for (let m = 1; m <= endMonth; m++) {
    periods.push([year, m]);
  }
  return periods;
}

/**
 * Build comparison periods { previous: [[y,m],...], current: [[y,m],...] }
 * for every supported comparison type.
 *
 * Uses session.year / session.period_value as the reference point (latest period).
 *
 * Supported types (same as Trends page):
 *   ytd_vs_ytd, last_month_vs_ly, last_3m_vs_ly, last_6m_vs_ly,
 *   last_month_vs_prev, last_3m_vs_prev, last_6m_vs_prev, last_12m_vs_prev
 */
function buildComparisonPeriods(periodRef, session, metadata) {
  if (!session || !metadata?.pl) return null;

  const endYear = session.year;
  const endMonth = session.period_value;

  switch (periodRef) {
    case "ytd_vs_ytd": {
      const current = getYTDPeriods(endYear, endMonth);
      const previous = getYTDPeriods(endYear - 1, endMonth);
      return { previous, current };
    }

    case "last_month_vs_ly": {
      return {
        previous: [[endYear - 1, endMonth]],
        current: [[endYear, endMonth]],
      };
    }

    case "last_3m_vs_ly": {
      const current = getPeriodsRange(endYear, endMonth, 3);
      const previous = current.map(([y, m]) => [y - 1, m]);
      return { previous, current };
    }

    case "last_6m_vs_ly": {
      const current = getPeriodsRange(endYear, endMonth, 6);
      const previous = current.map(([y, m]) => [y - 1, m]);
      return { previous, current };
    }

    case "last_month_vs_prev": {
      const current = [[endYear, endMonth]];
      const previous = getPeriodsRange(endYear, endMonth, 1, 1);
      return { previous, current };
    }

    case "last_3m_vs_prev": {
      const current = getPeriodsRange(endYear, endMonth, 3, 0);
      const previous = getPeriodsRange(endYear, endMonth, 3, 3);
      return { previous, current };
    }

    case "last_6m_vs_prev": {
      const current = getPeriodsRange(endYear, endMonth, 6, 0);
      const previous = getPeriodsRange(endYear, endMonth, 6, 6);
      return { previous, current };
    }

    case "last_12m_vs_prev": {
      const current = getPeriodsRange(endYear, endMonth, 12, 0);
      const previous = getPeriodsRange(endYear, endMonth, 12, 12);
      return { previous, current };
    }

    default:
      return null;
  }
}

/**
 * Build a single-side period filter (for single-period P&L).
 * Returns the "current" side of the comparison.
 */
function buildPeriodFilter(periodRef, session, metadata) {
  const periods = buildComparisonPeriods(periodRef, session, metadata);
  return periods?.current || [];
}

/**
 * Data Orchestrator Hook for Alignment Session
 *
 * Manages page-level data loading with smart progressive prefetching.
 * Loads data for a SINGLE selected periodReference at a time.
 * When the period changes, all caches are cleared and loading restarts
 * from the current page forward.
 *
 * Usage in AlignmentSession:
 *   const orchestrator = useDataOrchestrator({ activeSession, pnlMetadata, periodReference });
 *   orchestrator.setCurrentPage('overview', ['overview', 'highlights', …]);
 */
export function useDataOrchestrator({
  activeSession,
  pnlMetadata,
  periodReference,
}) {
  // ========================= Refs for current values =========================
  const activeSessionRef = useRef(activeSession);
  activeSessionRef.current = activeSession;
  const pnlMetadataRef = useRef(pnlMetadata);
  pnlMetadataRef.current = pnlMetadata;
  const periodReferenceRef = useRef(periodReference);
  periodReferenceRef.current = periodReference;

  // Generation counter – incremented on every session OR period change so that
  // stale async results are silently discarded.
  const generationRef = useRef(0);

  // ========================= Shared P&L Cache =========================
  const [pnlCache, setPnlCache] = useState({});
  const pnlCacheRef = useRef({});
  const pendingPnlRef = useRef(new Map());

  // ========================= Overview Chart Data Cache =========================
  const [overviewChartData, setOverviewChartData] = useState(null);
  const overviewChartDataRef = useRef(null);
  const pendingOverviewRef = useRef(null);

  // ========================= Highlights Data Cache =========================
  const [highlightsCache, setHighlightsCache] = useState({});
  const highlightsCacheRef = useRef({});
  const pendingHighlightsRef = useRef(new Map());

  // ========================= Dimension Data Cache =========================
  const [dimensionCache, setDimensionCache] = useState({});
  const dimensionCacheRef = useRef({});
  const pendingDimensionRef = useRef(new Map());

  // ========================= Top Elements Data Cache =========================
  const [topElementsCache, setTopElementsCache] = useState({});
  const topElementsCacheRef = useRef({});
  const pendingTopElementsRef = useRef(new Map());

  // ========================= Initiatives Data Cache =========================
  const [initiativesData, setInitiativesData] = useState(null);
  const initiativesDataRef = useRef(null);
  const pendingInitiativesRef = useRef(null);

  // ========================= Page Status =========================
  const [pageStatus, setPageStatus] = useState({});
  const pageStatusRef = useRef({});

  // ========================= Queue Management =========================
  const prefetchQueueRef = useRef([]);
  const isProcessingRef = useRef(false);
  const currentPageKeyRef = useRef(null);
  const sortedPageKeysRef = useRef([]);

  // Session + period tracking for cache invalidation
  const prevSessionKeyRef = useRef(null);
  const prevPeriodRef = useRef(null);

  // ========================= Helpers =========================

  const updatePageStatus = useCallback((pageKey, status) => {
    pageStatusRef.current = { ...pageStatusRef.current, [pageKey]: status };
    setPageStatus((prev) => {
      if (prev[pageKey] === status) return prev;
      return { ...prev, [pageKey]: status };
    });
  }, []);

  const clearAllCaches = useCallback(() => {
    setPnlCache({});
    pnlCacheRef.current = {};
    pendingPnlRef.current = new Map();
    setOverviewChartData(null);
    overviewChartDataRef.current = null;
    pendingOverviewRef.current = null;
    setHighlightsCache({});
    highlightsCacheRef.current = {};
    pendingHighlightsRef.current = new Map();
    setDimensionCache({});
    dimensionCacheRef.current = {};
    pendingDimensionRef.current = new Map();
    setTopElementsCache({});
    topElementsCacheRef.current = {};
    pendingTopElementsRef.current = new Map();
    setInitiativesData(null);
    initiativesDataRef.current = null;
    pendingInitiativesRef.current = null;
  }, []);

  /**
   * Full reset: clear all caches, increment generation, reset page statuses
   * to IDLE, and stop any in-flight queue processing.
   */
  const fullReset = useCallback(
    (sortedKeys) => {
      generationRef.current += 1;
      clearAllCaches();

      const freshStatus = {};
      (sortedKeys || sortedPageKeysRef.current).forEach((key) => {
        freshStatus[key] = STATUS.IDLE;
      });
      pageStatusRef.current = freshStatus;
      setPageStatus(freshStatus);
      isProcessingRef.current = false;
    },
    [clearAllCaches],
  );

  // ========================= Data Loaders =========================

  /**
   * Load P&L data for the current period. Deduplicates concurrent requests.
   * Uses the active periodReference to build the period filter.
   */
  const loadPnlPeriod = useCallback(async () => {
    const pRef = periodReferenceRef.current;

    // Cache hit
    if (pnlCacheRef.current[pRef]?.values !== undefined) {
      return pnlCacheRef.current[pRef].values;
    }

    // Dedup
    if (pendingPnlRef.current.has(pRef)) {
      return pendingPnlRef.current.get(pRef);
    }

    const session = activeSessionRef.current;
    const metadata = pnlMetadataRef.current;
    if (!session || !metadata?.pl) return null;

    const gen = generationRef.current;

    const promise = (async () => {
      const periodFilter = buildPeriodFilter(pRef, session, metadata);
      if (!periodFilter || periodFilter.length === 0) return null;

      const params = {
        currency_unit: metadata.pl.default_currency_unit || "BRL",
        common_filters: { period: periodFilter },
        common_filters_mode: { period: "items" },
        common_filters_sequence: ["period"],
        pl_filters: {},
        pl_filters_mode: {},
        pl_filters_sequence: [],
      };

      const response = await getPnlValues(params);

      if (gen !== generationRef.current) return null;

      const values = response?.pl ? { 1: response.pl } : null;

      const entry = { values, error: null };
      pnlCacheRef.current = {
        ...pnlCacheRef.current,
        [pRef]: entry,
      };
      setPnlCache((prev) => ({ ...prev, [pRef]: entry }));

      return values;
    })();

    pendingPnlRef.current.set(pRef, promise);

    try {
      return await promise;
    } catch (error) {
      if (gen !== generationRef.current) return null;

      const entry = { values: null, error };
      pnlCacheRef.current = {
        ...pnlCacheRef.current,
        [pRef]: entry,
      };
      setPnlCache((prev) => ({ ...prev, [pRef]: entry }));
      throw error;
    } finally {
      pendingPnlRef.current.delete(pRef);
    }
  }, []);

  /**
   * Load overview chart data (monthly volume, revenue, serve margin).
   * Deduplicates concurrent requests.
   */
  const loadOverviewChart = useCallback(async () => {
    if (overviewChartDataRef.current !== null) {
      return overviewChartDataRef.current;
    }

    if (pendingOverviewRef.current) {
      return pendingOverviewRef.current;
    }

    const session = activeSessionRef.current;
    if (!session) return null;

    const gen = generationRef.current;

    const promise = (async () => {
      const response = await getOverviewData(
        session.year,
        session.period_value,
      );

      if (gen !== generationRef.current) return null;

      const data = response.monthly_data || [];
      overviewChartDataRef.current = data;
      setOverviewChartData(data);
      return data;
    })();

    pendingOverviewRef.current = promise;

    try {
      return await promise;
    } catch (error) {
      if (gen !== generationRef.current) return null;
      overviewChartDataRef.current = [];
      setOverviewChartData([]);
      throw error;
    } finally {
      pendingOverviewRef.current = null;
    }
  }, []);

  /**
   * Load highlights data for the current period reference.
   * Fetches two comparative P&Ls + margin drivers in parallel.
   */
  const loadHighlightsData = useCallback(async () => {
    const pRef = periodReferenceRef.current;

    if (highlightsCacheRef.current[pRef]) {
      return highlightsCacheRef.current[pRef];
    }

    if (pendingHighlightsRef.current.has(pRef)) {
      return pendingHighlightsRef.current.get(pRef);
    }

    const session = activeSessionRef.current;
    const metadata = pnlMetadataRef.current;
    if (!session || !metadata?.pl) return null;

    const gen = generationRef.current;
    const periods = buildComparisonPeriods(pRef, session, metadata);
    if (
      !periods ||
      periods.current.length === 0 ||
      periods.previous.length === 0
    )
      return null;

    const currencyUnit = metadata.pl.default_currency_unit || "BRL";

    const promise = (async () => {
      const base = {
        currency_unit: currencyUnit,
        pl_filters: {},
        pl_filters_mode: {},
        pl_filters_sequence: [],
      };

      const [resPrev, resCurr, resDrivers] = await Promise.all([
        getPnlValues({
          ...base,
          common_filters: { period: periods.previous },
          common_filters_mode: { period: "items" },
          common_filters_sequence: ["period"],
        }),
        getPnlValues({
          ...base,
          common_filters: { period: periods.current },
          common_filters_mode: { period: "items" },
          common_filters_sequence: ["period"],
        }),
        getDriversData({
          currency_unit: currencyUnit,
          common_filters_p1: { period: periods.previous },
          common_filters_mode_p1: { period: "items" },
          common_filters_sequence_p1: ["period"],
          common_filters_p2: { period: periods.current },
          common_filters_mode_p2: { period: "items" },
          common_filters_sequence_p2: ["period"],
        }),
      ]);

      if (gen !== generationRef.current) return null;

      const result = {
        plValues: { 1: resPrev?.pl || {}, 2: resCurr?.pl || {} },
        driversData: resDrivers || null,
      };

      highlightsCacheRef.current = {
        ...highlightsCacheRef.current,
        [pRef]: result,
      };
      setHighlightsCache((prev) => ({ ...prev, [pRef]: result }));
      return result;
    })();

    pendingHighlightsRef.current.set(pRef, promise);

    try {
      return await promise;
    } catch (error) {
      if (gen !== generationRef.current) return null;
      const fallback = { plValues: { 1: {}, 2: {} }, driversData: null };
      highlightsCacheRef.current = {
        ...highlightsCacheRef.current,
        [pRef]: fallback,
      };
      setHighlightsCache((prev) => ({ ...prev, [pRef]: fallback }));
      throw error;
    } finally {
      pendingHighlightsRef.current.delete(pRef);
    }
  }, []);

  /**
   * Load dimension breakdown data for the current period.
   */
  const loadDimensionData = useCallback(async () => {
    const pRef = periodReferenceRef.current;

    if (dimensionCacheRef.current[pRef]) {
      return dimensionCacheRef.current[pRef];
    }

    if (pendingDimensionRef.current.has(pRef)) {
      return pendingDimensionRef.current.get(pRef);
    }

    const session = activeSessionRef.current;
    const metadata = pnlMetadataRef.current;
    if (!session || !metadata?.pl) return null;

    const gen = generationRef.current;
    const periods = buildComparisonPeriods(pRef, session, metadata);
    if (
      !periods ||
      periods.current.length === 0 ||
      periods.previous.length === 0
    )
      return null;

    const promise = (async () => {
      const result = await getDimensionData({
        period_current: periods.current,
        period_previous: periods.previous,
        dimensions: [],
      });

      if (gen !== generationRef.current) return null;

      dimensionCacheRef.current = {
        ...dimensionCacheRef.current,
        [pRef]: result,
      };
      setDimensionCache((prev) => ({ ...prev, [pRef]: result }));
      return result;
    })();

    pendingDimensionRef.current.set(pRef, promise);

    try {
      return await promise;
    } catch (error) {
      if (gen !== generationRef.current) return null;
      const fallback = { total: null, dimensions: {}, dimension_names: {} };
      dimensionCacheRef.current = {
        ...dimensionCacheRef.current,
        [pRef]: fallback,
      };
      setDimensionCache((prev) => ({ ...prev, [pRef]: fallback }));
      throw error;
    } finally {
      pendingDimensionRef.current.delete(pRef);
    }
  }, []);

  /**
   * Load top-elements data (ranking, cascade, mix) for a given element type
   * using the current periodReference.
   *
   * @param {"clients"|"products"} elementType
   */
  const loadTopElementsData = useCallback(async (elementType) => {
    const pRef = periodReferenceRef.current;
    const cacheKey = `${elementType}_${pRef}`;

    if (topElementsCacheRef.current[cacheKey]) {
      return topElementsCacheRef.current[cacheKey];
    }
    if (pendingTopElementsRef.current.has(cacheKey)) {
      return pendingTopElementsRef.current.get(cacheKey);
    }

    const session = activeSessionRef.current;
    const metadata = pnlMetadataRef.current;
    if (!session || !metadata?.pl) return null;

    const gen = generationRef.current;
    const periods = buildComparisonPeriods(pRef, session, metadata);
    if (
      !periods ||
      periods.current.length === 0 ||
      periods.previous.length === 0
    )
      return null;

    const promise = (async () => {
      const result = await getTopElementsData({
        element_type: elementType,
        period_current: periods.current,
        period_previous: periods.previous,
        top_n: 100,
      });

      if (gen !== generationRef.current) return null;

      topElementsCacheRef.current = {
        ...topElementsCacheRef.current,
        [cacheKey]: result,
      };
      setTopElementsCache((prev) => ({ ...prev, [cacheKey]: result }));
      return result;
    })();

    pendingTopElementsRef.current.set(cacheKey, promise);

    try {
      return await promise;
    } catch (error) {
      if (gen !== generationRef.current) return null;
      const fallback = {
        ranking: [],
        cascade: null,
        company_mix: {},
        total_comparison: {},
      };
      topElementsCacheRef.current = {
        ...topElementsCacheRef.current,
        [cacheKey]: fallback,
      };
      setTopElementsCache((prev) => ({ ...prev, [cacheKey]: fallback }));
      throw error;
    } finally {
      pendingTopElementsRef.current.delete(cacheKey);
    }
  }, []);

  /**
   * Load initiatives data. Reuses the existing action_plans_initiatives endpoint.
   */
  const loadInitiatives = useCallback(async () => {
    if (initiativesDataRef.current !== null) {
      return initiativesDataRef.current;
    }
    if (pendingInitiativesRef.current) {
      return pendingInitiativesRef.current;
    }

    const gen = generationRef.current;

    const promise = (async () => {
      const result = await getInitiativesData();
      if (gen !== generationRef.current) return null;
      initiativesDataRef.current = result;
      setInitiativesData(result);
      return result;
    })();

    pendingInitiativesRef.current = promise;

    try {
      return await promise;
    } catch (error) {
      if (gen !== generationRef.current) return null;
      initiativesDataRef.current = { initiatives: {} };
      setInitiativesData({ initiatives: {} });
      throw error;
    } finally {
      pendingInitiativesRef.current = null;
    }
  }, []);

  /**
   * Load all data required by a specific page.
   * Uses the CURRENT periodReference only – no prefetch of other periods.
   */
  const loadPageData = useCallback(
    async (pageKey) => {
      switch (pageKey) {
        case "overview": {
          await Promise.all([loadPnlPeriod(), loadOverviewChart()]);
          break;
        }

        case "highlights": {
          await loadHighlightsData();
          break;
        }

        case "by_dimension": {
          await loadDimensionData();
          break;
        }

        case "top_customers": {
          await loadTopElementsData("clients");
          break;
        }

        case "top_products": {
          await loadTopElementsData("products");
          break;
        }

        case "initiatives": {
          await loadInitiatives();
          break;
        }

        default:
          break;
      }
    },
    [
      loadPnlPeriod,
      loadOverviewChart,
      loadHighlightsData,
      loadDimensionData,
      loadTopElementsData,
      loadInitiatives,
    ],
  );

  // ========================= Queue Processing =========================

  const getNextPageToLoad = useCallback(() => {
    for (const pageKey of prefetchQueueRef.current) {
      const status = pageStatusRef.current[pageKey];
      if (
        status !== STATUS.READY &&
        status !== STATUS.LOADING &&
        status !== STATUS.ERROR
      ) {
        return pageKey;
      }
    }
    return null;
  }, []);

  const processQueue = useCallback(async () => {
    if (isProcessingRef.current) return;
    isProcessingRef.current = true;

    let nextPage = getNextPageToLoad();

    while (nextPage) {
      updatePageStatus(nextPage, STATUS.LOADING);

      try {
        await loadPageData(nextPage);
        updatePageStatus(nextPage, STATUS.READY);
      } catch (error) {
        console.error(
          `[DataOrchestrator] Error loading page "${nextPage}":`,
          error,
        );
        updatePageStatus(nextPage, STATUS.ERROR);
      }

      nextPage = getNextPageToLoad();
    }

    isProcessingRef.current = false;
  }, [getNextPageToLoad, updatePageStatus, loadPageData]);

  // ========================= Public API =========================

  /**
   * Notify the orchestrator of the current page.
   * Call this whenever the user navigates OR whenever session/metadata/period change.
   */
  const setCurrentPage = useCallback(
    (pageKey, sortedKeys) => {
      currentPageKeyRef.current = pageKey;
      sortedPageKeysRef.current = sortedKeys;

      // --- detect session change → full reset ---
      const session = activeSessionRef.current;
      const sessionKey = session
        ? `${session.id}_${session.year}_${session.period_value}`
        : null;

      if (sessionKey && sessionKey !== prevSessionKeyRef.current) {
        prevSessionKeyRef.current = sessionKey;
        prevPeriodRef.current = periodReferenceRef.current;
        fullReset(sortedKeys);
      }

      // --- detect period change → full reset (keep session key) ---
      const currentPeriod = periodReferenceRef.current;
      if (currentPeriod && currentPeriod !== prevPeriodRef.current) {
        prevPeriodRef.current = currentPeriod;
        fullReset(sortedKeys);
      }

      // --- ensure every page key has a status entry ---
      const currentStatus = { ...pageStatusRef.current };
      let statusChanged = false;
      sortedKeys.forEach((key) => {
        if (!(key in currentStatus)) {
          currentStatus[key] = STATUS.IDLE;
          statusChanged = true;
        }
      });
      if (statusChanged) {
        pageStatusRef.current = currentStatus;
        setPageStatus(currentStatus);
      }

      // --- build new prefetch queue & start processing ---
      prefetchQueueRef.current = buildPrefetchQueue(sortedKeys, pageKey);

      if (!isProcessingRef.current) {
        processQueue();
      }
    },
    [fullReset, processQueue],
  );

  /**
   * Is a page's data ready? (state-based – triggers re-render)
   */
  const isPageReady = useCallback(
    (pageKey) => pageStatus[pageKey] === STATUS.READY,
    [pageStatus],
  );

  /**
   * Is a page's data ready? (ref-based – no re-render wait)
   */
  const isPageReadySync = useCallback(
    (pageKey) => pageStatusRef.current[pageKey] === STATUS.READY,
    [],
  );

  /**
   * Is a page currently loading or not yet loaded?
   */
  const isPageLoading = useCallback(
    (pageKey) => {
      const status = pageStatus[pageKey];
      return !status || status === STATUS.IDLE || status === STATUS.LOADING;
    },
    [pageStatus],
  );

  /**
   * Get cached P&L data for the current period reference.
   */
  const getCachedPnlData = useCallback(
    () => pnlCache[periodReference]?.values || null,
    [pnlCache, periodReference],
  );

  /**
   * Is P&L currently being fetched?
   */
  const isPnlLoading = useCallback(
    () => pendingPnlRef.current.has(periodReference),
    [periodReference],
  );

  /**
   * Get overview chart data.
   */
  const getOverviewChartData = useCallback(
    () => overviewChartData,
    [overviewChartData],
  );

  /**
   * Clear all caches and reset.
   */
  const clearCache = useCallback(() => {
    fullReset();
    prefetchQueueRef.current = [];
  }, [fullReset]);

  // ========================= Derived state =========================

  const isLoading = useMemo(
    () => Object.values(pageStatus).some((s) => s === STATUS.LOADING),
    [pageStatus],
  );

  const isDataReady = useMemo(
    () =>
      Object.keys(pageStatus).length > 0 &&
      Object.values(pageStatus).every(
        (s) => s === STATUS.READY || s === STATUS.ERROR,
      ),
    [pageStatus],
  );

  // ========================= Kick-start on mount / changes =========================

  useEffect(() => {
    if (
      activeSession &&
      pnlMetadata?.pl &&
      periodReference &&
      currentPageKeyRef.current &&
      sortedPageKeysRef.current.length > 0
    ) {
      setCurrentPage(currentPageKeyRef.current, sortedPageKeysRef.current);
    }
    // eslint-disable-next-line
  }, [
    activeSession?.id,
    activeSession?.year,
    activeSession?.period_value,
    pnlMetadata,
    periodReference,
  ]);

  // ========================= Return =========================

  return {
    // Page status
    setCurrentPage,
    isPageReady,
    isPageReadySync,
    isPageLoading,
    pageStatus,

    // Shared P&L data (single period)
    getCachedPnlData,
    isPnlLoading,
    loadPnlData: loadPnlPeriod,

    // Overview chart data
    overviewData: overviewChartData,
    getOverviewChartData,

    // Highlights data
    getHighlightsData: useCallback(
      () => highlightsCache[periodReference] || null,
      [highlightsCache, periodReference],
    ),
    loadHighlightsData,

    // Dimension data
    getDimensionData: useCallback(
      () => dimensionCache[periodReference] || null,
      [dimensionCache, periodReference],
    ),
    loadDimensionData,

    // Top elements data
    getTopElementsData: useCallback(
      (elementType) =>
        topElementsCache[`${elementType}_${periodReference}`] || null,
      [topElementsCache, periodReference],
    ),
    loadTopElementsData,

    // Initiatives data
    initiativesData,

    // Comparison periods (for sections that need prev/current labels)
    getComparisonPeriods: useCallback(() => {
      const session = activeSessionRef.current;
      const metadata = pnlMetadataRef.current;
      return buildComparisonPeriods(periodReference, session, metadata);
    }, [periodReference]),

    // Global loading states
    isLoading,
    isDataReady,

    // Cache management
    clearCache,
  };
}

export default useDataOrchestrator;
